import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { buildFinancialApiUrl } from '../utils/financial-api-url.util';
import { ErrorService } from '../shared/services/error.service';
import { VehiclesService } from './vehicles.service';

export interface TrafficFinePagedResult {
  items: any[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface TrafficFineUnlinkedItem {
  id: number;
  aipNumber: string;
  placa: string;
  contractId: number;
  contractNumber: string;
  contratante: string;
  infractionDateTime?: string | null;
}

export interface PaginationOptions {
  page?: number;
  pageSize?: number;
}

/** Campos normalizados do card "Infração" na tela de detalhe. */
export interface TrafficFineInfractionInfo {
  description: string;
  city: string;
  state: string;
  infractionStatus: string;
  infractionDateTime: any;
}

/** Query da listagem GET /trafficfines (paginação + filtros no backend). */
export interface TrafficFineListQuery extends PaginationOptions {
  /** Nome ou ID do cliente. */
  cliente?: string;
  placa?: string;
  maiorValor?: number;
  /** Data da infração — yyyy-MM-dd (input) ou dd/MM/yyyy */
  dataInfracao?: string;
  /** Número do AIT (AIPNumber). */
  aipNumber?: string;
  /** Período: início — yyyy-MM-dd (input) ou dd/MM/yyyy */
  periodoInicio?: string;
  /** Período: fim — yyyy-MM-dd (input) ou dd/MM/yyyy */
  periodoFim?: string;
}

export interface DateRangeOptions {
  startDate?: string;
  endDate?: string;
}

// --- DTOs de Dashboard ---

export interface TrafficFineDashboardSummaryDTO {
  totalFines?: number;

  // Totais (contrato atual do backend)
  totalOriginalAmount?: number;
  totalAmountToClient?: number;
  totalAmountPaid?: number;
  totalPendingAmount?: number;

  // Contagens e valores por status (contrato atual do backend)
  countAPagar?: number;
  countPagoPelaEmpresa?: number;
  countPagoPeloCliente?: number;
  countReembolsado?: number;
  amountAPagar?: number;
  amountPagoPelaEmpresa?: number;
  amountPagoPeloCliente?: number;

  // Vencimento e atraso
  countOverdue?: number;
  amountOverdue?: number;
  countDueNext7Days?: number;
  amountDueNext7Days?: number;
  countDueNext30Days?: number;
  amountDueNext30Days?: number;

  // Séries agregadas (quando o endpoint /dashboard retornar tudo)
  finesByMonth?: TrafficFineByPeriodDTO[];
  amountByMonth?: TrafficFineByPeriodDTO[];
  finesByPaymentStatus?: TrafficFineByStatusDTO[];
  finesByInfractionStatus?: TrafficFineByStatusDTO[];
  top10VehiclesWithMostFines?: TrafficFineByVehicleDTO[];

  // Compat/legado (mantido para não quebrar se backend retornar formato antigo)
  totalAmount?: number;
  pendingCount?: number;
  pendingAmount?: number;
  paidCount?: number;
  paidAmount?: number;
  overdueCount?: number;
  overdueAmount?: number;
  upcomingDueCount?: number;
  upcomingDueAmount?: number;
  [key: string]: any;
}

export interface TrafficFineByPeriodDTO {
  label: string;
  count: number;
  year?: number;
  month?: number;
  totalOriginalAmount?: number;
  totalFinalAmountToClient?: number;
  totalAmountPaid?: number;

  // compat
  totalAmount?: number;
  [key: string]: any;
}

export interface TrafficFineByStatusDTO {
  status: string;
  count: number;
  totalOriginalAmount?: number;
  percentage?: number;

  // compat
  totalAmount?: number;
  [key: string]: any;
}

export interface TrafficFineHistoryDto {
  id: number;
  description?: string;
  userName: string;
  statusTrafficFine?: string;
  date: string;
  trafficFineId: number;
  createdAt: string;
  createdBy: string;
}

export interface TrafficFineByVehicleDTO {
  vehicleId: number;
  // backend atual retorna plateNumber / vehicleDescription / totais
  plateNumber?: string;
  vehicleDescription?: string;
  count: number;
  totalOriginalAmount?: number;
  totalFinalAmountToClient?: number;

  // compat: contratos antigos / versões anteriores
  vehiclePlate?: string;
  totalAmount?: number;
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class TrafficFinesService {
  private apiUrl = `${environment.microservices.financial}/trafficfines`;

  private financialUrl(path = ''): string {
    return buildFinancialApiUrl('trafficfines', path);
  }

  constructor(
    private http: HttpClient,
    private errorService: ErrorService,
    private vehiclesService: VehiclesService
  ) {}

  // --- CRUD ---

  getAll(options?: TrafficFineListQuery): Observable<TrafficFinePagedResult> {
    let params = this.buildPaginationParams(options);
    params = this.appendListQueryParams(params, options);
    return this.http.get<any>(`${this.financialUrl()}`, { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar multas de trânsito');
        return throwError(() => err);
      })
    );
  }

  listSemVinculo(): Observable<{ total: number; itens: TrafficFineUnlinkedItem[] }> {
    return this.http.get<any>(this.financialUrl('/sem-vinculo')).pipe(
      map(body => ({
        total: Number(body?.total ?? body?.Total ?? 0),
        itens: (body?.itens ?? body?.Itens ?? []).map((item: any) => ({
          id: Number(item.id ?? item.Id),
          aipNumber: item.aipNumber ?? item.AipNumber ?? '',
          placa: item.placa ?? item.Placa ?? '',
          contractId: Number(item.contractId ?? item.ContractId ?? 0),
          contractNumber: item.contractNumber ?? item.ContractNumber ?? '',
          contratante: item.contratante ?? item.Contratante ?? '',
          infractionDateTime: item.infractionDateTime ?? item.InfractionDateTime ?? null
        }))
      })),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar multas sem vínculo');
        return throwError(() => err);
      })
    );
  }

  /**
   * @param listSnapshot Linha da listagem (quando navega da tabela), para campos que o GET por id não traz.
   */
  getById(id: number, listSnapshot?: any): Observable<any> {
    return this.http.get<any>(this.financialUrl(`/${id}`)).pipe(
      map(resp => this.normalizeFineItem(this.unwrapSingleResponse(resp))),
      switchMap(fine => this.enrichFineWithMissingFields(fine, listSnapshot)),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar multa de trânsito');
        return throwError(() => err);
      })
    );
  }

  obterIndicacao(id: number): Observable<{ indicationLimitAt: string | null; indicatedAt: string | null }> {
    return this.http
      .get<{ indicationLimitAt: string | null; indicatedAt: string | null }>(this.financialUrl(`/${id}/indicacao`))
      .pipe(catchError(err => throwError(() => err)));
  }

  enviarResumoEmail(id: number, email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(this.financialUrl(`/${id}/enviar-email`), { email }).pipe(
      catchError(err => throwError(() => err))
    );
  }

  getHistories(id: number): Observable<TrafficFineHistoryDto[]> {
    return this.http.get<TrafficFineHistoryDto[]>(this.financialUrl(`/${id}/histories`)).pipe(
      map(resp => {
        if (Array.isArray(resp)) {
          return resp;
        }
        const items = (resp as any)?.items ?? (resp as any)?.Items ?? (resp as any)?.data ?? (resp as any)?.Data;
        return Array.isArray(items) ? items : [];
      }),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar histórico da multa');
        return throwError(() => err);
      })
    );
  }

  create(payload: any): Observable<any> {
    return this.http.post<any>(`${this.financialUrl()}`, payload).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao criar multa de trânsito');
        return throwError(() => err);
      })
    );
  }

  update(id: number, payload: any): Observable<any> {
    return this.http.put<any>(this.financialUrl(`/${id}`), payload).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao atualizar multa de trânsito');
        return throwError(() => err);
      })
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(this.financialUrl(`/${id}`)).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao excluir multa de trânsito');
        return throwError(() => err);
      })
    );
  }

  // --- Filtros especializados ---

  getByBank(bank: string, options?: PaginationOptions): Observable<TrafficFinePagedResult> {
    const params = this.buildPaginationParams(options);
    return this.http.get<any>(this.financialUrl(`/bank/${encodeURIComponent(bank)}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar multas por banco');
        return throwError(() => err);
      })
    );
  }

  getPendingByVehicle(vehicleId: number, options?: PaginationOptions): Observable<TrafficFinePagedResult> {
    const params = this.buildPaginationParams(options);
    return this.http.get<any>(this.financialUrl(`/pending/vehicle/${vehicleId}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar multas pendentes por veículo');
        return throwError(() => err);
      })
    );
  }

  getPendingByDueRange(start: string, end: string, options?: PaginationOptions): Observable<TrafficFinePagedResult> {
    let params = new HttpParams().set('start', start).set('end', end);
    params = this.appendPaginationParams(params, options);
    return this.http.get<any>(this.financialUrl('/pending/due-range'), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar multas pendentes por período');
        return throwError(() => err);
      })
    );
  }

  getPaidManual(options?: PaginationOptions): Observable<TrafficFinePagedResult> {
    const params = this.buildPaginationParams(options);
    return this.http.get<any>(this.financialUrl('/paid/manual'), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar multas pagas manualmente');
        return throwError(() => err);
      })
    );
  }

  getDebitedAutomatic(options?: PaginationOptions): Observable<TrafficFinePagedResult> {
    const params = this.buildPaginationParams(options);
    return this.http.get<any>(this.financialUrl('/debited/automatic'), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar multas debitadas automaticamente');
        return throwError(() => err);
      })
    );
  }

  getByContract(contractNumber: string, options?: PaginationOptions): Observable<TrafficFinePagedResult> {
    const params = this.buildPaginationParams(options);
    return this.http.get<any>(this.financialUrl(`/contract/${encodeURIComponent(contractNumber)}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar multas por contrato');
        return throwError(() => err);
      })
    );
  }

  getByCustomer(customerId: number, options?: PaginationOptions): Observable<TrafficFinePagedResult> {
    const params = this.buildPaginationParams(options);
    return this.http.get<any>(this.financialUrl(`/customer/${customerId}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar multas por cliente');
        return throwError(() => err);
      })
    );
  }

  getByInfractionStatus(status: string, options?: PaginationOptions): Observable<TrafficFinePagedResult> {
    const params = this.buildPaginationParams(options);
    return this.http.get<any>(this.financialUrl(`/infraction-status/${encodeURIComponent(status)}`), { params }).pipe(
      map(resp => this.mapPagedResponse(resp)),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar multas por status de infração');
        return throwError(() => err);
      })
    );
  }

  // --- Dashboard ---

  getDashboardSummary(startDate?: string, endDate?: string): Observable<TrafficFineDashboardSummaryDTO> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http.get<TrafficFineDashboardSummaryDTO>(this.financialUrl('/dashboard'), { params }).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar resumo do dashboard de multas');
        return throwError(() => err);
      })
    );
  }

  getDashboardByMonth(months: number = 12): Observable<TrafficFineByPeriodDTO[]> {
    const params = new HttpParams().set('months', months.toString());
    return this.http.get<TrafficFineByPeriodDTO[]>(this.financialUrl('/dashboard/by-month'), { params }).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar multas por mês');
        return throwError(() => err);
      })
    );
  }

  getDashboardByPaymentStatus(startDate?: string, endDate?: string): Observable<TrafficFineByStatusDTO[]> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http.get<TrafficFineByStatusDTO[]>(this.financialUrl('/dashboard/by-payment-status'), { params }).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar multas por status de pagamento');
        return throwError(() => err);
      })
    );
  }

  getDashboardByInfractionStatus(startDate?: string, endDate?: string): Observable<TrafficFineByStatusDTO[]> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http.get<TrafficFineByStatusDTO[]>(this.financialUrl('/dashboard/by-infraction-status'), { params }).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar multas por status de infração (dash)');
        return throwError(() => err);
      })
    );
  }

  getDashboardTopVehicles(top: number = 10, startDate?: string, endDate?: string): Observable<TrafficFineByVehicleDTO[]> {
    let params = new HttpParams().set('top', top.toString());
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http.get<any>(this.financialUrl('/dashboard/top-vehicles'), { params }).pipe(
      map(resp => {
        // backend pode retornar array direto ou envelopado
        if (Array.isArray(resp)) return resp as TrafficFineByVehicleDTO[];
        if (Array.isArray(resp?.items)) return resp.items as TrafficFineByVehicleDTO[];
        if (Array.isArray(resp?.Items)) return resp.Items as TrafficFineByVehicleDTO[];
        if (Array.isArray(resp?.data)) return resp.data as TrafficFineByVehicleDTO[];
        return [] as TrafficFineByVehicleDTO[];
      }),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar top veículos com multas');
        return throwError(() => err);
      })
    );
  }

  // --- Helpers privados ---

  private buildPaginationParams(options?: PaginationOptions): HttpParams {
    return this.appendPaginationParams(new HttpParams(), options);
  }

  private appendPaginationParams(params: HttpParams, options?: PaginationOptions): HttpParams {
    if (options?.page) params = params.set('page', options.page.toString());
    if (options?.pageSize) params = params.set('pageSize', options.pageSize.toString());
    return params;
  }

  private appendListQueryParams(params: HttpParams, options?: TrafficFineListQuery): HttpParams {
    if (!options) {
      return params;
    }
    const cliente = options.cliente?.trim();
    if (cliente) {
      params = params.set('cliente', cliente);
    }
    const placa = options.placa?.trim();
    if (placa) {
      params = params.set('placa', placa.replace(/\s|-/g, '').toUpperCase());
    }
    if (options.maiorValor != null && !Number.isNaN(options.maiorValor) && options.maiorValor > 0) {
      params = params.set('maiorValor', String(options.maiorValor));
    }
    const dataInfracao = this.formatListDateForApi(options.dataInfracao);
    if (dataInfracao) {
      params = params.set('dataInfracao', dataInfracao);
    }
    const aipNumber = options.aipNumber?.trim();
    if (aipNumber) {
      params = params.set('aipNumber', aipNumber);
    }
    const periodoInicio = this.formatListDateForApi(options.periodoInicio);
    if (periodoInicio) {
      params = params.set('periodoInicio', periodoInicio);
    }
    const periodoFim = this.formatListDateForApi(options.periodoFim);
    if (periodoFim) {
      params = params.set('periodoFim', periodoFim);
    }
    return params;
  }

  /** Converte yyyy-MM-dd (input date) para dd/MM/yyyy exigido pela API de listagem. */
  private formatListDateForApi(value?: string): string | null {
    const trimmed = value?.trim();
    if (!trimmed) {
      return null;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [year, month, day] = trimmed.split('-');
      return `${day}/${month}/${year}`;
    }
    return trimmed;
  }

  /** GET por id pode vir envelopado (data/Data) e com placa em nomes diferentes da listagem. */
  private unwrapSingleResponse(response: any): any {
    if (response == null || typeof response !== 'object') {
      return response;
    }
    const inner = response.data ?? response.Data;
    if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
      return inner;
    }
    return response;
  }

  /** Dados do card Infração já resolvidos (várias chaves da API → nomes canônicos). */
  getInfractionInfo(item: any | null | undefined): TrafficFineInfractionInfo {
    return this.extractInfractionInfo(item);
  }

  resolveAccountsReceivableId(fine: any): number | null {
    const raw = fine?.accountsReceivableId ?? fine?.AccountsReceivableId;
    if (raw == null || raw === '') {
      return null;
    }
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  /**
   * Alinha campos do detalhe com a listagem (placa, infração, cliente, etc.).
   */
  normalizeFineItem(item: any): any {
    if (!item || typeof item !== 'object') {
      return item;
    }

    const plate = this.extractPlate(item);
    const infraction = this.extractInfractionInfo(item);
    const vehicle = item.vehicle ?? item.Vehicle;

    const customerName =
      item.customerName ??
      item.CustomerName ??
      item.customer?.name ??
      item.customer?.Name ??
      item.Customer?.name ??
      item.Customer?.Name ??
      '';

    const vehicleDescription =
      item.vehicleDescription ??
      item.VehicleDescription ??
      vehicle?.description ??
      vehicle?.Description ??
      (vehicle?.brand || vehicle?.Brand
        ? `${vehicle?.brand ?? vehicle?.Brand ?? ''} ${vehicle?.model ?? vehicle?.Model ?? ''}`.trim()
        : '') ??
      '';

    const contractNumber =
      item.contractNumber ??
      item.ContractNumber ??
      item.contract?.number ??
      item.contract?.Number ??
      item.Contract?.number ??
      item.Contract?.Number ??
      '';

    const accountsReceivableId = this.resolveAccountsReceivableId(item);

    return {
      ...item,
      vehiclePlate: plate || item.vehiclePlate || item.VehiclePlate || '',
      plateNumber: plate || item.plateNumber || item.PlateNumber || '',
      customerName: customerName || item.customerName || item.CustomerName || '',
      vehicleDescription: vehicleDescription || item.vehicleDescription || item.VehicleDescription || '',
      contractNumber: contractNumber || item.contractNumber || item.ContractNumber || '',
      accountsReceivableId: accountsReceivableId ?? undefined,
      AccountsReceivableId: accountsReceivableId ?? undefined,
      description: infraction.description,
      city: infraction.city,
      state: infraction.state,
      infractionStatus: infraction.infractionStatus,
      infractionDateTime:
        infraction.infractionDateTime ??
        item.infractionDateTime ??
        item.InfractionDateTime ??
        item.issueDate ??
        item.IssueDate
    };
  }

  extractInfractionInfo(item: any): TrafficFineInfractionInfo {
    if (!item || typeof item !== 'object') {
      return this.emptyInfractionInfo();
    }

    const nested = item.infraction ?? item.Infraction;
    const locationParsed = this.parseCityState(
      this.pickFirstString(item, [
        'infractionLocation',
        'InfractionLocation',
        'location',
        'Location',
        'address',
        'Address'
      ]) ||
        this.pickFirstString(nested, ['location', 'Location', 'infractionLocation', 'InfractionLocation'])
    );

    const description = this.pickFirstString(item, [
      'description',
      'Description',
      'infractionDescription',
      'InfractionDescription',
      'offenseDescription',
      'OffenseDescription',
      'violationDescription',
      'ViolationDescription',
      'infractionDetails',
      'InfractionDetails',
      'details',
      'Details',
      'observation',
      'Observation',
      'notes',
      'Notes'
    ]) ||
      this.pickFirstString(nested, [
        'description',
        'Description',
        'infractionDescription',
        'InfractionDescription'
      ]);

    const city =
      this.pickFirstString(item, [
        'city',
        'City',
        'infractionCity',
        'InfractionCity',
        'municipality',
        'Municipality',
        'municipio',
        'Municipio',
        'cityName',
        'CityName'
      ]) ||
      this.pickFirstString(nested, ['city', 'City', 'municipality', 'Municipality']) ||
      locationParsed.city;

    const state =
      this.pickFirstString(item, [
        'state',
        'State',
        'infractionState',
        'InfractionState',
        'uf',
        'UF',
        'stateCode',
        'StateCode'
      ]) ||
      this.pickFirstString(nested, ['state', 'State', 'uf', 'UF']) ||
      locationParsed.state;

    const infractionStatus = this.pickFirstString(item, [
      'infractionStatus',
      'InfractionStatus',
      'statusInfraction',
      'StatusInfraction',
      'infringementStatus',
      'InfringementStatus'
    ]) || this.pickFirstString(nested, ['status', 'Status', 'infractionStatus', 'InfractionStatus']);

    const infractionDateTime = this.pickFirstRaw(item, [
      'infractionDateTime',
      'InfractionDateTime',
      'issueDate',
      'IssueDate',
      'infractionDate',
      'InfractionDate',
      'registrationDate',
      'RegistrationDate',
      'autuationDate',
      'AutuationDate'
    ]) ?? this.pickFirstRaw(nested, ['date', 'Date', 'infractionDateTime', 'InfractionDateTime']);

    return {
      description,
      city,
      state,
      infractionStatus,
      infractionDateTime
    };
  }

  /** GET /{id} costuma não trazer todos os campos; listagem e veículo complementam. */
  private enrichFineWithMissingFields(fine: any, listSnapshot?: any): Observable<any> {
    let merged = this.normalizeFineItem(this.mergeListSnapshot(fine, listSnapshot));

    // Com snapshot da navegação (ex.: B2B), evita getAll(200) pesado que parece “travar”.
    const needsList =
      !listSnapshot && (!this.hasPlate(merged) || !this.hasInfractionCoreFields(merged));

    const afterList$ = needsList ? this.resolveFromListEndpoint(merged) : of(merged);

    return afterList$.pipe(
      switchMap(withList => {
        merged = this.normalizeFineItem(withList);
        if (this.hasPlate(merged)) {
          return of(merged);
        }
        return this.resolvePlateFromVehicle(merged);
      }),
      map(result => this.normalizeFineItem(result))
    );
  }

  private resolvePlateFromVehicle(fine: any): Observable<any> {
    const vehicleId = this.getVehicleIdFromFine(fine);
    if (!vehicleId) {
      return of(fine);
    }

    return this.vehiclesService.getById(vehicleId).pipe(
      map(vehicle => {
        const plate = (vehicle?.plateNumber || '').trim();
        if (!plate) {
          return fine;
        }
        return this.normalizeFineItem({
          ...fine,
          vehicleId,
          vehiclePlate: plate,
          plateNumber: plate,
          vehicleDescription:
            fine.vehicleDescription ||
            fine.VehicleDescription ||
            `${vehicle.brand || ''} ${vehicle.model || ''}`.trim()
        });
      }),
      catchError(() => of(fine))
    );
  }

  /** Listagem traz mais campos; busca o mesmo registro por id/AIT quando o GET por id não traz. */
  private resolveFromListEndpoint(fine: any): Observable<any> {
    const fineId = fine?.id ?? fine?.Id;
    const ait = fine?.aipNumber ?? fine?.AIPNumber;
    if (!fineId && !ait) {
      return of(fine);
    }

    return this.getAll({ page: 1, pageSize: 200 }).pipe(
      map(res => {
        const match = (res.items || []).find((row: any) => {
          const rowId = row?.id ?? row?.Id;
          if (fineId != null && rowId != null && Number(rowId) === Number(fineId)) {
            return true;
          }
          const rowAit = row?.aipNumber ?? row?.AIPNumber;
          return ait != null && rowAit != null && String(rowAit).trim() === String(ait).trim();
        });
        return match ? this.mergeListSnapshot(fine, match) : fine;
      }),
      catchError(() => of(fine))
    );
  }

  private mergeListSnapshot(fine: any, listSnapshot?: any): any {
    if (!listSnapshot) {
      return fine;
    }

    const base = this.extractInfractionInfo(fine);
    const list = this.extractInfractionInfo(listSnapshot);
    const plate = this.extractPlate(listSnapshot) || this.extractPlate(fine);

    return {
      ...fine,
      ...listSnapshot,
      vehiclePlate: plate || fine?.vehiclePlate,
      plateNumber: plate || fine?.plateNumber,
      customerName:
        this.coalesceText(fine?.customerName ?? fine?.CustomerName, listSnapshot.customerName ?? listSnapshot.CustomerName),
      vehicleDescription: this.coalesceText(
        fine?.vehicleDescription ?? fine?.VehicleDescription,
        listSnapshot.vehicleDescription ?? listSnapshot.VehicleDescription
      ),
      contractNumber: this.coalesceText(
        fine?.contractNumber ?? fine?.ContractNumber,
        listSnapshot.contractNumber ?? listSnapshot.ContractNumber
      ),
      description: this.coalesceText(base.description, list.description),
      city: this.coalesceText(base.city, list.city),
      state: this.coalesceText(base.state, list.state),
      infractionStatus: this.coalesceText(base.infractionStatus, list.infractionStatus),
      infractionDateTime: base.infractionDateTime ?? list.infractionDateTime ?? listSnapshot.infractionDateTime ?? listSnapshot.issueDate,
      driverIndicationId: this.coalesceNumericId(
        fine?.driverIndicationId ?? fine?.DriverIndicationId,
        listSnapshot?.driverIndicationId ?? listSnapshot?.DriverIndicationId
      ),
      driverName: this.coalesceText(fine?.driverName ?? fine?.DriverName, listSnapshot?.driverName ?? listSnapshot?.DriverName),
      driverCpf: this.coalesceText(fine?.driverCpf ?? fine?.DriverCpf, listSnapshot?.driverCpf ?? listSnapshot?.DriverCpf),
      driverRg: this.coalesceText(fine?.driverRg ?? fine?.DriverRg, listSnapshot?.driverRg ?? listSnapshot?.DriverRg),
      driverAddress: this.coalesceText(
        fine?.driverAddress ?? fine?.DriverAddress,
        listSnapshot?.driverAddress ?? listSnapshot?.DriverAddress
      ),
      driverCep: this.coalesceText(fine?.driverCep ?? fine?.DriverCep, listSnapshot?.driverCep ?? listSnapshot?.DriverCep),
      driverCity: this.coalesceText(fine?.driverCity ?? fine?.DriverCity, listSnapshot?.driverCity ?? listSnapshot?.DriverCity),
      driverState: this.coalesceText(fine?.driverState ?? fine?.DriverState, listSnapshot?.driverState ?? listSnapshot?.DriverState),
      driverCnhNumber: this.coalesceText(
        fine?.driverCnhNumber ?? fine?.DriverCnhNumber,
        listSnapshot?.driverCnhNumber ?? listSnapshot?.DriverCnhNumber
      ),
      driverCnhRegister: this.coalesceText(
        fine?.driverCnhRegister ?? fine?.DriverCnhRegister,
        listSnapshot?.driverCnhRegister ?? listSnapshot?.DriverCnhRegister
      ),
      driverCnhUf: this.coalesceText(fine?.driverCnhUf ?? fine?.DriverCnhUf, listSnapshot?.driverCnhUf ?? listSnapshot?.DriverCnhUf),
      accountsReceivableId: this.coalesceNumericId(
        fine?.accountsReceivableId ?? fine?.AccountsReceivableId,
        listSnapshot?.accountsReceivableId ?? listSnapshot?.AccountsReceivableId
      ),
      AccountsReceivableId: this.coalesceNumericId(
        fine?.accountsReceivableId ?? fine?.AccountsReceivableId,
        listSnapshot?.accountsReceivableId ?? listSnapshot?.AccountsReceivableId
      )
    };
  }

  private coalesceNumericId(primary: any, fallback: any): number | null {
    for (const raw of [primary, fallback]) {
      if (raw == null || raw === '') {
        continue;
      }
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) {
        return n;
      }
    }
    return null;
  }

  private hasInfractionCoreFields(item: any): boolean {
    const info = this.extractInfractionInfo(item);
    return !!(
      info.description ||
      info.infractionStatus ||
      (info.city && info.state)
    );
  }

  private emptyInfractionInfo(): TrafficFineInfractionInfo {
    return {
      description: '',
      city: '',
      state: '',
      infractionStatus: '',
      infractionDateTime: null
    };
  }

  private coalesceText(primary: any, fallback: any): string {
    const p = primary != null ? String(primary).trim() : '';
    if (p) {
      return p;
    }
    return fallback != null ? String(fallback).trim() : '';
  }

  private pickFirstString(obj: any, keys: string[]): string {
    if (!obj) {
      return '';
    }
    for (const key of keys) {
      const value = obj[key];
      if (value != null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    return '';
  }

  private pickFirstRaw(obj: any, keys: string[]): any {
    if (!obj) {
      return null;
    }
    for (const key of keys) {
      const value = obj[key];
      if (value != null && value !== '') {
        return value;
      }
    }
    return null;
  }

  private parseCityState(location?: string): { city: string; state: string } {
    if (!location || typeof location !== 'string') {
      return { city: '', state: '' };
    }
    const trimmed = location.trim();
    const parts = trimmed.split(/\s*[-/,|]\s*/).map(p => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const last = parts[parts.length - 1];
      const isUf = /^[A-Za-z]{2}$/.test(last);
      return {
        city: isUf ? parts.slice(0, -1).join(' - ') : parts[0],
        state: isUf ? last.toUpperCase() : parts[1] ?? ''
      };
    }
    return { city: trimmed, state: '' };
  }

  private extractPlate(item: any): string {
    if (!item || typeof item !== 'object') {
      return '';
    }

    const vehicle = item.vehicle ?? item.Vehicle;
    const contract = item.contract ?? item.Contract;
    const direct =
      item.vehiclePlate ??
      item.VehiclePlate ??
      item.plateNumber ??
      item.PlateNumber ??
      item.plate ??
      item.Plate ??
      item.placa ??
      item.Placa ??
      item.licensePlate ??
      item.LicensePlate ??
      vehicle?.plateNumber ??
      vehicle?.PlateNumber ??
      vehicle?.plate ??
      vehicle?.Plate ??
      vehicle?.placa ??
      vehicle?.Placa ??
      vehicle?.vehiclePlate ??
      vehicle?.VehiclePlate ??
      contract?.vehiclePlate ??
      contract?.VehiclePlate ??
      contract?.plateNumber ??
      contract?.PlateNumber ??
      '';

    const normalized = String(direct ?? '').trim();
    if (normalized) {
      return normalized;
    }

    return this.deepFindPlate(item);
  }

  private deepFindPlate(obj: any, depth = 0, seen = new Set<any>()): string {
    if (!obj || typeof obj !== 'object' || depth > 5 || seen.has(obj)) {
      return '';
    }
    seen.add(obj);

    for (const [key, value] of Object.entries(obj)) {
      if (value == null || value === '') {
        continue;
      }
      const keyLower = key.toLowerCase();
      if (
        typeof value === 'string' &&
        (keyLower.includes('plate') || keyLower.includes('placa')) &&
        !keyLower.includes('template') &&
        this.looksLikeVehiclePlate(value)
      ) {
        return value.trim();
      }
    }

    for (const value of Object.values(obj)) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const found = this.deepFindPlate(value, depth + 1, seen);
        if (found) {
          return found;
        }
      }
    }

    return '';
  }

  private looksLikeVehiclePlate(value: string): boolean {
    const compact = value.replace(/\s|-|_/g, '').toUpperCase();
    return compact.length >= 7 && compact.length <= 8 && /^[A-Z0-9]+$/.test(compact);
  }

  private hasPlate(item: any): boolean {
    return !!this.extractPlate(item);
  }

  private getVehicleIdFromFine(item: any): number | null {
    const raw =
      item?.vehicleId ??
      item?.VehicleId ??
      item?.vehicle?.id ??
      item?.vehicle?.Id ??
      item?.Vehicle?.id ??
      item?.Vehicle?.Id;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  private mapPagedResponse(response: any): TrafficFinePagedResult {
    const rawItems = Array.isArray(response?.Items)
      ? response.Items
      : Array.isArray(response?.items)
        ? response.items
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];

    const page = response?.Page ?? response?.page ?? 1;
    const pageSize = response?.PageSize ?? response?.pageSize ?? rawItems.length;
    const totalItems = response?.TotalItems ?? response?.totalItems ?? response?.TotalRecords ?? response?.totalRecords ?? rawItems.length;
    const totalPages = response?.TotalPages ?? response?.totalPages ?? (pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1);

    const items = rawItems.map((row: any) => this.normalizeFineItem(row));

    return { items, page, pageSize, totalItems, totalPages };
  }
}
